// src/components/ClassStudents.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import { doc, setDoc, updateDoc, collection, query, where, documentId, onSnapshot } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { StudentContext } from '../StudentContext';
import './ClassStudents.css';

// Import các Tab
import AttendanceSundayTab from './classStudents/AttendanceSundayTab';
import AttendanceCatechismTab from './classStudents/AttendanceCatechismTab';
import AttendanceWeekdayTab from './classStudents/AttendanceWeekdayTab';
import StatisticsTab from './classStudents/StatisticsTab';
import ProfileTab from './classStudents/ProfileTab';
import TuitionFeeTab from './classStudents/TuitionFeeTab';

const ClassStudents = () => {
  const { blockId, classId } = useParams();
  const navigate = useNavigate();

  const {
    currentYearId,
    studentsData,
    classMap,
    dateLocks, // 🌟 Lấy trạng thái khóa ngày từ StudentContext
    isLoading: isContextLoading
  } = useContext(StudentContext);

  // State lưu trữ dữ liệu điểm danh realtime lấy từ Firestore
  const [classAttendances, setClassAttendances] = useState({});

  // State UI & Filters
  const [activeTab, setActiveTab] = useState('church');
  const [selectedChurchDate, setSelectedChurchDate] = useState('');
  const [selectedCatechismDate, setSelectedCatechismDate] = useState('');
  const [selectedWeekdayDate, setSelectedWeekdayDate] = useState('');
  const [numDatesToShow, setNumDatesToShow] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // State Modal Chỉnh Sửa
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editDateOfBirth, setEditDateOfBirth] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editHolyName, setEditHolyName] = useState('');
  const [editGender, setEditGender] = useState('nam');
  const [editAttendance, setEditAttendance] = useState('');
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState('');
  const [modalMode, setModalMode] = useState('dob'); // 'dob' | 'status' | 'holyName' | 'gender' | 'churchAttendance' | 'catechismAttendance'

  const displayClassName = classMap[classId] || classId;
  const rawClassStudents = studentsData[classId] || [];

  // 1. Sắp xếp danh sách học sinh theo tên (A-Z)
  const sortedStudents = useMemo(() => {
    const getLastNamePart = (fullName) => {
      if (!fullName) return '';
      const parts = fullName.trim().split(' ');
      return parts[parts.length - 1].toLowerCase();
    };

    return [...rawClassStudents].sort((a, b) => {
      const nameA = getLastNamePart(a.fullName);
      const nameB = getLastNamePart(b.fullName);
      return nameA.localeCompare(nameB, 'vi', { sensitivity: 'base' });
    });
  }, [rawClassStudents]);

  // 2. LẤY DANH SÁCH ID HỌC SINH ĐỂ LẮNG NGHE FIRESTORE
  const studentIds = useMemo(() => {
    return sortedStudents.map((s) => s.id).filter(Boolean);
  }, [sortedStudents]);

  // 3. LẮNG NGHE REALTIME COLLECTION student_attendances CỦA LỚP HIỆN TẠI
  useEffect(() => {
    if (!currentYearId || studentIds.length === 0) {
      setClassAttendances({});
      return;
    }

    const chunks = [];
    for (let i = 0; i < studentIds.length; i += 30) {
      chunks.push(studentIds.slice(i, i + 30));
    }

    const unsubs = chunks.map((chunk) => {
      const q = query(
        collection(firestore, `academic_years/${currentYearId}/student_attendances`),
        where(documentId(), 'in', chunk)
      );

      return onSnapshot(q, (snapshot) => {
        setClassAttendances((prev) => {
          const updated = { ...prev };
          snapshot.docs.forEach((docSnap) => {
            updated[docSnap.id] = docSnap.data();
          });
          return updated;
        });
      }, (error) => {
        console.error('Lỗi lắng nghe điểm danh của lớp:', error);
      });
    });

    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [currentYearId, studentIds]);

  // 4. GHÉP DỮ LIỆU ĐIỂM DANH VÀO HỌC SINH
  const students = useMemo(() => {
    const baseList = (activeTab === 'profile' || activeTab === 'tuition')
      ? sortedStudents
      : sortedStudents.filter((s) => (s.status || '').trim().toLowerCase() !== 'nghỉ luôn');

    return baseList.map((student) => {
      const attData = classAttendances[student.id] || {};
      return {
        ...student,
        attendance: attData.church || {},               // Đi Lễ (Chúa Nhật + Ngày Thường)
        catechismAttendance: attData.catechism || {},   // Học Giáo Lý
      };
    });
  }, [sortedStudents, activeTab, classAttendances]);

  // 5. TỰ ĐỘNG TẬP HỢP TẤT CẢ CÁC NGÀY ĐIỂM DANH ĐÃ CÓ TRONG FIRESTORE
  const isSunday = (dateStr) => {
    if (!dateStr) return false;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return false;
    return new Date(parts[0], parts[1] - 1, parts[2]).getDay() === 0;
  };

  const { sundayDates, weekdayDates, catechismAttendanceDates } = useMemo(() => {
    const churchDateSet = new Set();
    const catechismDateSet = new Set();

    Object.values(classAttendances).forEach((att) => {
      if (att.church) {
        Object.keys(att.church).forEach((date) => churchDateSet.add(date));
      }
      if (att.catechism) {
        Object.keys(att.catechism).forEach((date) => catechismDateSet.add(date));
      }
    });

    const sortedChurch = Array.from(churchDateSet).sort();
    const sortedCatechism = Array.from(catechismDateSet).sort();

    return {
      sundayDates: sortedChurch.filter((d) => isSunday(d)),
      weekdayDates: sortedChurch.filter((d) => !isSunday(d)),
      catechismAttendanceDates: sortedCatechism,
    };
  }, [classAttendances]);

  const displayedSundayDates = sundayDates.slice(-numDatesToShow).reverse();
  const displayedCatechismDates = catechismAttendanceDates.slice(-numDatesToShow).reverse();
  const displayedWeekdayDates = weekdayDates.slice(-numDatesToShow).reverse();

  // Handlers
  const handleChurchDateChange = (e) => setSelectedChurchDate(e.target.value);
  const handleCatechismDateChange = (e) => setSelectedCatechismDate(e.target.value);
  const handleStudentClick = (sId) => navigate(`/main/student/${sId}`);
  const handleTranscriptView = () => navigate(`/main/class-transcript/${blockId}/${classId}`);

  const handleEditDob = (student) => {
    setSelectedStudent(student);
    setModalMode('dob');
    setEditDateOfBirth(student.dateOfBirth || '');
    setIsModalOpen(true);
  };

  const handleEditStatus = (student) => {
    setSelectedStudent(student);
    setModalMode('status');
    setEditStatus(student.status || 'Còn đi học');
    setIsModalOpen(true);
  };

  const handleEditHolyName = (student) => {
    setSelectedStudent(student);
    setModalMode('holyName');
    setEditHolyName(student.holyName || '');
    setIsModalOpen(true);
  };

  const handleEditGender = (student) => {
    setSelectedStudent(student);
    setModalMode('gender');
    setEditGender(student.gender || 'nam');
    setIsModalOpen(true);
  };

  const handleEditAttendance = (student, date, type) => {
    setSelectedStudent(student);
    setSelectedAttendanceDate(date);
    setModalMode(type === 'church' ? 'churchAttendance' : 'catechismAttendance');
    setEditAttendance(
      type === 'church'
        ? student.attendance?.[date]?.time || 'Vắng không xin phép'
        : student.catechismAttendance?.[date]?.time || 'Vắng không xin phép'
    );
    setIsModalOpen(true);
  };

  const handleSaveDob = async () => {
    try {
      await updateDoc(doc(firestore, 'students', selectedStudent.id), { dateOfBirth: editDateOfBirth });
      setIsModalOpen(false);
    } catch (err) {
      alert('Lỗi cập nhật ngày sinh: ' + err.message);
    }
  };

  const handleSaveStatus = async () => {
    try {
      await updateDoc(doc(firestore, 'students', selectedStudent.id), { status: editStatus });
      setIsModalOpen(false);
    } catch (err) {
      alert('Lỗi cập nhật trạng thái: ' + err.message);
    }
  };

  const handleSaveHolyName = async () => {
    try {
      await updateDoc(doc(firestore, 'students', selectedStudent.id), { holyName: editHolyName });
      setIsModalOpen(false);
    } catch (err) {
      alert('Lỗi cập nhật tên thánh: ' + err.message);
    }
  };

  const handleSaveGender = async () => {
    try {
      await updateDoc(doc(firestore, 'students', selectedStudent.id), { gender: editGender });
      setIsModalOpen(false);
    } catch (err) {
      alert('Lỗi cập nhật giới tính: ' + err.message);
    }
  };

  // 6. LƯU ĐIỂM DANH THỦ CÔNG (BAO GỒM "CÓ ĐIỂM DANH")
  const handleSaveAttendance = async () => {
    try {
      if (!currentYearId || !selectedAttendanceDate || !selectedStudent) return;

      // Kiểm tra chặn nếu ngày bị khóa mà chọn Có điểm danh
      if (dateLocks?.[selectedAttendanceDate] && editAttendance === 'Có điểm danh') {
        alert('Ngày này đã bị khóa điểm danh bổ sung!');
        return;
      }

      const isChurch = modalMode === 'churchAttendance';
      const fieldGroup = isChurch ? 'church' : 'catechism';
      const catechistName = localStorage.getItem('catechistFullName') || 'Giáo lý viên';

      const studentAttRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, selectedStudent.id);

      await setDoc(studentAttRef, {
        [fieldGroup]: {
          [selectedAttendanceDate]: {
            time: editAttendance,
            catechist: catechistName,
            time_log: new Date().toISOString(),
          }
        }
      }, { merge: true });

      setIsModalOpen(false);
    } catch (err) {
      alert('Lỗi khi lưu điểm danh: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  const handleExportToExcel = () => {
    let data = [];
    let header = [];

    if (activeTab === 'church') {
      const exportSundayDates = displayedSundayDates.slice().reverse();
      header = ['ID', 'Tên Thánh', 'Họ và Tên', ...exportSundayDates];
      data = students.map((s) => [
        s.id,
        s.holyName || '',
        s.fullName,
        ...exportSundayDates.map((date) => s.attendance?.[date]?.time || 'Vắng không xin phép'),
      ]);
    } else if (activeTab === 'catechism') {
      const exportGlDates = displayedCatechismDates.slice().reverse();
      header = ['ID', 'Tên Thánh', 'Họ và Tên', ...exportGlDates];
      data = students.map((s) => [
        s.id,
        s.holyName || '',
        s.fullName,
        ...exportGlDates.map((date) => s.catechismAttendance?.[date]?.time || 'Vắng không xin phép'),
      ]);
    } else if (activeTab === 'profile') {
      header = ['ID', 'Tên Thánh', 'Họ và Tên', 'Giới Tính', 'Ngày Sinh', 'Tình trạng học tập'];
      data = students.map((s) => [
        s.id,
        s.holyName || '',
        s.fullName,
        s.gender === 'nu' ? 'Nữ' : 'Nam',
        s.dateOfBirth || '',
        s.status || 'Còn đi học'
      ]);
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    XLSX.utils.book_append_sheet(wb, ws, 'Danh sách');
    XLSX.writeFile(wb, `danh_sach_${displayClassName.replace(/\s+/g, '_')}.xlsx`);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    const days = ['Chúa Nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
    return `${days[dateObj.getDay()]} - ${day}/${month}/${year}`;
  };

  const formatDate = (dob) => dob || 'Chưa có';

  // 🌟 CẬP NHẬT: Nhận diện trạng thái "Có điểm danh" (Màu xanh lá)
  const getAttendanceClass = (status) => {
    let className = 'attendance-button';
    const trimmedStatus = String(status || 'Vắng không xin phép').trim();
    const isScanPresent = trimmedStatus.startsWith('Có lúc');
    const isManualPresent = trimmedStatus === 'Có điểm danh';

    if (trimmedStatus === 'Chưa điểm danh' || trimmedStatus === 'Vắng không xin phép') {
      className += ' attendance-chua-diem-danh';
    } else if (trimmedStatus === 'Vắng có xin phép') {
      className += ' attendance-co-xin-phep';
    } else if (isScanPresent || isManualPresent) {
      className += ' attendance-co-luc'; // Màu xanh lá cây
    }

    // Nếu đã quét QR ("Có lúc...") thì disable, còn "Có điểm danh" bổ sung thì vẫn cho phép sửa nếu ngày chưa khóa
    const isDisabled = isScanPresent;
    return { className, isDisabled };
  };

  const getDetailedStats = (attendanceData, dates, start, end) => {
    if (!attendanceData || !dates) return { total: 0, excused: 0, unexcused: 0 };
    const filteredDates = dates.filter((date) => {
      const pureDate = date.substring(0, 10);
      return (!start || pureDate >= start) && (!end || pureDate <= end);
    });

    let excused = 0;
    let unexcused = 0;
    filteredDates.forEach((date) => {
      const status = attendanceData[date]?.time || 'Vắng không xin phép';
      if (status === 'Vắng có xin phép') excused++;
      else if (status === 'Chưa điểm danh' || status === 'Vắng không xin phép') unexcused++;
    });

    return { total: excused + unexcused, excused, unexcused };
  };

  // Kiểm tra xem ngày hiện tại trong modal có bị khóa hay không
  const isCurrentDateLocked = Boolean(dateLocks?.[selectedAttendanceDate]);

  return (
    <div className="class-content cs-wrapper">
      <h3 className="cs-title">Danh sách lớp: {displayClassName}</h3>

      <div className="cs-button-group">
        <button className="cs-btn cs-btn-transcript" onClick={handleTranscriptView}>
          📊 Xem Bảng Điểm
        </button>
        <button className="cs-btn cs-btn-excel" onClick={handleExportToExcel}>
          📥 Tải File Excel
        </button>
      </div>

      <nav className="cs-tabs-container">
        <div className="cs-tabs-scroll">
          <button
            className={`cs-nav-tab ${activeTab === 'church' ? 'active' : ''}`}
            onClick={() => setActiveTab('church')}
          >
            ⛪ Lễ Chúa Nhật
          </button>
          <button
            className={`cs-nav-tab ${activeTab === 'catechism' ? 'active' : ''}`}
            onClick={() => setActiveTab('catechism')}
          >
            📖 Học Giáo Lý
          </button>
          <button
            className={`cs-nav-tab ${activeTab === 'weekdayChurch' ? 'active' : ''}`}
            onClick={() => setActiveTab('weekdayChurch')}
          >
            📅 Lễ Ngày Thường
          </button>
          <button
            className={`cs-nav-tab ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            📊 Thống Kê
          </button>
          <button
            className={`cs-nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            📋 Sơ Yếu Lý Lịch
          </button>
          <button
            className={`cs-nav-tab ${activeTab === 'tuition' ? 'active' : ''}`}
            onClick={() => setActiveTab('tuition')}
          >
            💰 Đóng Học Phí
          </button>
        </div>
      </nav>

      {isContextLoading ? (
        <div className="loading-message">⏳ Đang đồng bộ dữ liệu...</div>
      ) : sortedStudents.length > 0 ? (
        <div className="table-wrapper">
          {activeTab !== 'profile' && activeTab !== 'statistics' && activeTab !== 'tuition' && (
            <div className="date-display-controls">
              <label htmlFor="numDatesInput">Số cột ngày muốn hiển thị:</label>
              <input
                id="numDatesInput"
                type="number"
                value={numDatesToShow}
                onChange={(e) => setNumDatesToShow(Number(e.target.value) || 1)}
                min="1"
                max={50}
              />
            </div>
          )}

          {activeTab === 'church' && (
            <AttendanceSundayTab
              students={students}
              sundayDates={sundayDates}
              selectedChurchDate={selectedChurchDate || (sundayDates[sundayDates.length - 1] || '')}
              handleChurchDateChange={handleChurchDateChange}
              displayedSundayDates={displayedSundayDates}
              handleStudentClick={handleStudentClick}
              getAttendanceClass={getAttendanceClass}
              handleEditAttendance={handleEditAttendance}
              formatDisplayDate={formatDisplayDate}
              dateLocks={dateLocks} // 🌟 Truyền trạng thái khóa ngày
            />
          )}

          {activeTab === 'catechism' && (
            <AttendanceCatechismTab
              students={students}
              catechismAttendanceDates={catechismAttendanceDates}
              selectedCatechismDate={selectedCatechismDate || (catechismAttendanceDates[catechismAttendanceDates.length - 1] || '')}
              handleCatechismDateChange={handleCatechismDateChange}
              displayedCatechismDates={displayedCatechismDates}
              handleStudentClick={handleStudentClick}
              getAttendanceClass={getAttendanceClass}
              handleEditAttendance={handleEditAttendance}
              formatDisplayDate={formatDisplayDate}
              dateLocks={dateLocks} // 🌟 Truyền trạng thái khóa ngày
            />
          )}

          {activeTab === 'weekdayChurch' && (
            <AttendanceWeekdayTab
              students={students}
              weekdayDates={weekdayDates}
              selectedWeekdayDate={selectedWeekdayDate || (weekdayDates[weekdayDates.length - 1] || '')}
              setSelectedWeekdayDate={setSelectedWeekdayDate}
              displayedWeekdayDates={displayedWeekdayDates}
              getAttendanceClass={getAttendanceClass}
              handleEditAttendance={handleEditAttendance}
              formatDisplayDate={formatDisplayDate}
            />
          )}

          {activeTab === 'statistics' && (
            <StatisticsTab
              students={students}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              getDetailedStats={getDetailedStats}
              sundayDates={sundayDates}
              catechismAttendanceDates={catechismAttendanceDates}
              weekdayDates={weekdayDates}
            />
          )}

          {activeTab === 'profile' && (
            <ProfileTab
              students={students}
              handleStudentClick={handleStudentClick}
              formatDate={formatDate}
              handleEditHolyName={handleEditHolyName}
              handleEditGender={handleEditGender}
              handleEditDob={handleEditDob}
              handleEditStatus={handleEditStatus}
            />
          )}

          {activeTab === 'tuition' && (
            <TuitionFeeTab
              students={students}
              classId={classId}
              displayClassName={displayClassName}
              currentYearId={currentYearId}
            />
          )}
        </div>
      ) : (
        <p className="no-data-message">Chưa có dữ liệu học sinh cho lớp {displayClassName}.</p>
      )}

      {/* Modal Popup Chỉnh Sửa */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            {selectedStudent && (
              <div className="student-info">
                <p><strong>ID:</strong> {selectedStudent.id}</p>
                <p><strong>Tên Thánh:</strong> {selectedStudent.holyName || 'Chưa có'}</p>
                <p><strong>Họ và Tên:</strong> {selectedStudent.fullName}</p>
                {selectedAttendanceDate && (
                  <p>
                    <strong>Ngày:</strong> {formatDisplayDate(selectedAttendanceDate)}
                    {isCurrentDateLocked && (
                      <span style={{ color: '#dc2626', fontWeight: 'bold', marginLeft: '6px' }}>
                        🔒 (Đã khóa)
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
            <h3>
              {modalMode === 'dob'
                ? 'Chỉnh sửa ngày sinh'
                : modalMode === 'status'
                  ? 'Chỉnh sửa tình trạng học tập'
                  : modalMode === 'holyName'
                    ? 'Chỉnh sửa tên thánh'
                    : modalMode === 'gender'
                      ? 'Chỉnh sửa giới tính'
                      : 'Chỉnh sửa điểm danh'}
            </h3>
            <div className="modal-body">
              {modalMode === 'dob' ? (
                <label>
                  Ngày sinh:
                  <input
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={editDateOfBirth}
                    onChange={(e) => setEditDateOfBirth(e.target.value)}
                  />
                </label>
              ) : modalMode === 'gender' ? (
                <div className="status-controls-syll">
                  <label>
                    <input
                      type="radio"
                      name="gender"
                      value="nam"
                      checked={editGender === 'nam'}
                      onChange={(e) => setEditGender(e.target.value)}
                    />
                    Nam 👦
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="gender"
                      value="nu"
                      checked={editGender === 'nu' || editGender === 'nữ'}
                      onChange={(e) => setEditGender(e.target.value)}
                    />
                    Nữ 👧
                  </label>
                </div>
              ) : modalMode === 'status' ? (
                <div className="status-controls-syll">
                  <label>
                    <input
                      type="radio"
                      name="status"
                      value="Còn đi học"
                      checked={editStatus === 'Còn đi học'}
                      onChange={(e) => setEditStatus(e.target.value)}
                    />
                    Còn đi học
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="status"
                      value="Nghỉ luôn"
                      checked={editStatus === 'Nghỉ luôn'}
                      onChange={(e) => setEditStatus(e.target.value)}
                    />
                    Nghỉ luôn
                  </label>
                </div>
              ) : modalMode === 'holyName' ? (
                <label>
                  Tên Thánh:
                  <input
                    type="text"
                    value={editHolyName}
                    onChange={(e) => setEditHolyName(e.target.value)}
                  />
                </label>
              ) : (
                <div className="attendance-controls modal-body-option">
                  {/* 🌟 1. LỰA CHỌN "CÓ ĐIỂM DANH" - ẨN KHI NGÀY ĐÃ BỊ KHÓA */}
                  {!isCurrentDateLocked && (
                    <label>
                      <input
                        type="radio"
                        name="attendance"
                        value="Có điểm danh"
                        checked={editAttendance === 'Có điểm danh'}
                        onChange={(e) => setEditAttendance(e.target.value)}
                      />
                      ✅ Có điểm danh (Bổ sung)
                    </label>
                  )}

                  {isCurrentDateLocked && (
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#fef2f2',
                      border: '1px dashed #f87171',
                      borderRadius: '8px',
                      color: '#b91c1c',
                      fontSize: '13px',
                      fontWeight: 600,
                      marginBottom: '6px'
                    }}>
                      🔒 Ngày này đã bị khóa. Không thể chọn "Có điểm danh" bổ sung!
                    </div>
                  )}

                  {/* 2. VẮNG CÓ XIN PHÉP */}
                  <label>
                    <input
                      type="radio"
                      name="attendance"
                      value="Vắng có xin phép"
                      checked={editAttendance === 'Vắng có xin phép'}
                      onChange={(e) => setEditAttendance(e.target.value)}
                    />
                    ⚠️ Vắng có xin phép
                  </label>

                  {/* 3. VẮNG KHÔNG XIN PHÉP */}
                  <label>
                    <input
                      type="radio"
                      name="attendance"
                      value="Vắng không xin phép"
                      checked={editAttendance === 'Vắng không xin phép'}
                      onChange={(e) => setEditAttendance(e.target.value)}
                    />
                    ❌ Vắng không xin phép
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="save-button"
                onClick={
                  modalMode === 'dob'
                    ? handleSaveDob
                    : modalMode === 'status'
                      ? handleSaveStatus
                      : modalMode === 'holyName'
                        ? handleSaveHolyName
                        : modalMode === 'gender'
                          ? handleSaveGender
                          : handleSaveAttendance
                }
              >
                Lưu
              </button>
              <button className="cancel-button" onClick={handleCloseModal}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassStudents;